/**
 * Constants for the Exotel Web Calling (WebRTC) softphone.
 *
 * The event and registration strings below are the ones the Exotel SDK
 * actually emits — they were read off the SDK source rather than its README,
 * which is out of date on both counts. See `helper/exotelWebPhone.js`.
 */

// Emitted by ExotelWebPhoneSDK's call listener callback.
export const SDK_CALL_EVENTS = {
  INCOMING: 'incoming',
  CONNECTED: 'connected',
  CALL_ENDED: 'callEnded',
  HOLD_TOGGLE: 'holdtoggle',
  MUTE_TOGGLE: 'mutetoggle',
};

// Emitted by the registration callback. Note there is deliberately no
// "failed" state here, and the two SDK generations differ in how a failure
// shows up: the client bundled today (webrtc-client-sdk 2.x) reports a failed
// registration as "unregistered", indistinguishable from a deliberate sign
// off, while 3.x emits nothing at all. Both are handled in
// helper/exotelWebPhone.js — an "unregistered" seen before the phone has ever
// registered is a failure, and REGISTRATION_TIMEOUT below catches the silence.
export const SDK_REGISTRATION_EVENTS = {
  REGISTERED: 'registered',
  UNREGISTERED: 'unregistered',
  // Emitted while the REGISTER request is in flight; not a terminal state.
  SENT_REQUEST: 'sent_request',
};

// Our own view of the softphone, which adds the states the SDK cannot report.
export const REGISTRATION_STATES = {
  UNREGISTERED: 'unregistered',
  REGISTERING: 'registering',
  REGISTERED: 'registered',
  FAILED: 'failed',
};

export const CALL_STATES = {
  IDLE: 'idle',
  INCOMING: 'incoming',
  OUTGOING: 'outgoing',
  CONNECTED: 'connected',
};

export const CALL_DIRECTIONS = {
  INBOUND: 'inbound',
  OUTBOUND: 'outbound',
};

// How long to wait for a "registered" event before declaring the softphone
// dead. Registration normally completes in well under a second; anything past
// this is a blocked WebSocket, a bad token, or a firewall dropping the media
// ports, none of which the SDK surfaces as an event.
export const REGISTRATION_TIMEOUT = 15000;

// Cross-tab lease. Agents routinely keep several Chatwoot tabs open, and more
// than one registered softphone per agent means calls ring in an arbitrary tab
// or get answered twice. Exactly one tab holds the lease and registers.
export const PHONE_LEASE_KEY = 'exotelWebPhoneLease';
export const PHONE_LEASE_TTL = 15000;
export const PHONE_LEASE_HEARTBEAT = 5000;

// Per-agent "take calls / stop taking calls" switch. Kept per user id so two
// agents sharing a machine do not inherit each other's choice, and in local
// storage so it survives a reload — an agent who signed off does not want the
// phone ringing again because they refreshed the page.
export const PHONE_AVAILABILITY_KEY = 'exotelWebPhoneAvailability';
